<script setup lang="ts">
import Panel from 'primevue/panel';
import Avatar from 'primevue/avatar';
import SilkIcon from '~/components/icons/SilkIcon.vue';
import cdnAssetPath from '~/functions/cdnAssetPath';
import { formatDateString } from '~/functions/parseDateString';
import type { TournamentGamesProps } from './TournamentGamesProps';
import { getGroundName, getGroundDirectionsUrl, getClubName, getClubDirectionsUrl } from './tournamentGameHelpers';

defineProps<TournamentGamesProps>();

// Mobile uses shorter date format (no year)
const formatDateShort = (dateString: string) =>
   formatDateString(dateString, { month: 'short', day: 'numeric' });

function formatTime(timeString: string | null): string {
   if (!timeString) {
      return '-';
   }
   return timeString;
}

function getPlayerPhotoUrl(photo: string | null | undefined): string {
   if (!photo) return '';
   if (photo.startsWith('http://') || photo.startsWith('https://')) {
      return photo;
   }
   return cdnAssetPath(photo);
}
</script>

<template>
   <div class="flex flex-col gap-3">
      <!-- Loading State -->
      <div v-if="isLoading" class="space-y-3">
         <div v-for="i in 3" :key="i" class="animate-pulse rounded-lg bg-gray-100 p-4">
            <div class="mb-2 h-4 w-24 rounded bg-gray-200"></div>
            <div class="h-6 w-full rounded bg-gray-200"></div>
         </div>
      </div>

      <!-- Games List -->
      <Panel
         v-for="game in games"
         v-else
         :key="game.id"
         toggleable
         collapsed
         class="game-panel"
      >
         <template #header>
            <div class="flex w-full min-w-0 flex-col gap-1">
               <!-- Date, Time & Field Row -->
               <div class="flex items-center gap-1 text-xs text-gray-500">
                  <span>{{ formatDateShort(game.gameDate) }}</span>
                  <span v-if="game.time">· {{ formatTime(game.time) }}</span>
                  <span v-if="game.ground">· {{ getGroundName(game.ground) }}</span>
               </div>

               <!-- Team 1 with Score -->
               <div class="flex items-start justify-between gap-2">
                  <div class="flex min-w-0 flex-1 items-center gap-1">
                     <SilkIcon
                        v-if="game.teams.team1.shirtColors"
                        :primary-color="game.teams.team1.shirtColors.primary"
                        :secondary-color="game.teams.team1.shirtColors.secondary"
                        size="16px"
                        class="shrink-0"
                     />
                     <span
                        class="break-words text-sm font-medium uppercase"
                        :class="game.score.winnerIndex === 1 ? 'text-green-700' : 'text-gray-900'"
                     >
                        {{ game.teams.team1.teamName }}
                        <span v-if="game.teams.team1.hcp" class="text-xs font-normal text-gray-400">(HCP: {{ game.teams.team1.hcp }})</span>
                     </span>
                  </div>
                  <span
                     class="inline-flex size-6 shrink-0 items-center justify-center rounded text-xs font-bold text-white"
                     :class="game.score.winnerIndex === 1 ? 'bg-green-600' : 'bg-gray-400'"
                  >
                     {{ game.score.gameScore1 ?? '-' }}
                  </span>
               </div>

               <!-- Team 2 with Score -->
               <div class="flex items-start justify-between gap-2">
                  <div class="flex min-w-0 flex-1 items-center gap-1">
                     <SilkIcon
                        v-if="game.teams.team2.shirtColors"
                        :primary-color="game.teams.team2.shirtColors.primary"
                        :secondary-color="game.teams.team2.shirtColors.secondary"
                        size="16px"
                        class="shrink-0"
                     />
                     <span
                        class="break-words text-sm font-medium uppercase"
                        :class="game.score.winnerIndex === 2 ? 'text-green-700' : 'text-gray-900'"
                     >
                        {{ game.teams.team2.teamName }}
                        <span v-if="game.teams.team2.hcp" class="text-xs font-normal text-gray-400">(HCP: {{ game.teams.team2.hcp }})</span>
                     </span>
                  </div>
                  <span
                     class="inline-flex size-6 shrink-0 items-center justify-center rounded text-xs font-bold text-white"
                     :class="game.score.winnerIndex === 2 ? 'bg-green-600' : 'bg-gray-400'"
                  >
                     {{ game.score.gameScore2 ?? '-' }}
                  </span>
               </div>
            </div>
         </template>

         <template #default>
            <div class="space-y-3">
               <!-- Teams Section -->
               <div class="grid grid-cols-2 gap-2">
                  <!-- Team 1 -->
                  <div class="rounded-lg bg-gray-50 p-2">
                     <div class="mb-2 text-center">
                        <div class="flex items-center justify-center gap-1">
                           <SilkIcon
                              v-if="game.teams.team1.shirtColors"
                              :primary-color="game.teams.team1.shirtColors.primary"
                              :secondary-color="game.teams.team1.shirtColors.secondary"
                              size="18px"
                           />
                           <h4 class="truncate text-xs font-bold uppercase text-gray-700">
                              {{ game.teams.team1.teamName }}
                           </h4>
                        </div>
                        <span
                           class="mt-1 inline-flex size-7 items-center justify-center rounded text-sm font-bold text-white"
                           :class="game.score.winnerIndex === 1 ? 'bg-green-600' : 'bg-gray-400'"
                        >
                           {{ game.score.gameScore1 ?? '-' }}
                        </span>
                     </div>
                     <ul class="space-y-2">
                        <li
                           v-for="(player, idx) in game.teams.team1.players"
                           :key="idx"
                           class="flex items-center justify-between text-xs"
                        >
                           <div class="flex items-center gap-1">
                              <Avatar
                                 v-if="player.photo"
                                 :image="getPlayerPhotoUrl(player.photo)"
                                 :title="player.country ? `${player.player} (${player.country})` : player.player"
                                 shape="circle"
                                 size="small"
                                 :pt="{ image: { class: 'object-cover' } }"
                              />
                              <Avatar
                                 v-else
                                 icon="pi pi-user"
                                 :title="player.player"
                                 shape="circle"
                                 size="small"
                                 class="bg-gray-200"
                              />
                              <span class="text-gray-700">{{ player.player || '-' }}</span>
                           </div>
                           <span class="text-gray-400">({{ player.hcp || '-' }})</span>
                        </li>
                     </ul>
                  </div>

                  <!-- Team 2 -->
                  <div class="rounded-lg bg-gray-50 p-2">
                     <div class="mb-2 text-center">
                        <div class="flex items-center justify-center gap-1">
                           <SilkIcon
                              v-if="game.teams.team2.shirtColors"
                              :primary-color="game.teams.team2.shirtColors.primary"
                              :secondary-color="game.teams.team2.shirtColors.secondary"
                              size="18px"
                           />
                           <h4 class="truncate text-xs font-bold uppercase text-gray-700">
                              {{ game.teams.team2.teamName }}
                           </h4>
                        </div>
                        <span
                           class="mt-1 inline-flex size-7 items-center justify-center rounded text-sm font-bold text-white"
                           :class="game.score.winnerIndex === 2 ? 'bg-green-600' : 'bg-gray-400'"
                        >
                           {{ game.score.gameScore2 ?? '-' }}
                        </span>
                     </div>
                     <ul class="space-y-2">
                        <li
                           v-for="(player, idx) in game.teams.team2.players"
                           :key="idx"
                           class="flex items-center justify-between text-xs"
                        >
                           <div class="flex items-center gap-1">
                              <Avatar
                                 v-if="player.photo"
                                 :image="getPlayerPhotoUrl(player.photo)"
                                 :title="player.country ? `${player.player} (${player.country})` : player.player"
                                 shape="circle"
                                 size="small"
                                 :pt="{ image: { class: 'object-cover' } }"
                              />
                              <Avatar
                                 v-else
                                 icon="pi pi-user"
                                 :title="player.player"
                                 shape="circle"
                                 size="small"
                                 class="bg-gray-200"
                              />
                              <span class="text-gray-700">{{ player.player || '-' }}</span>
                           </div>
                           <span class="text-gray-400">({{ player.hcp || '-' }})</span>
                        </li>
                     </ul>
                  </div>
               </div>

               <!-- Game Details Section -->
               <div class="space-y-2 border-t pt-3">
                  <!-- Club -->
                  <div v-if="game.club" class="flex items-center gap-2 text-sm">
                     <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-blue-100">
                        <i class="pi pi-building text-[10px] text-blue-600"></i>
                     </span>
                     <div class="flex flex-wrap items-center gap-x-1">
                        <span class="text-gray-500">Club:</span>
                        <span class="text-gray-900">{{ getClubName(game.club) }}</span>
                        <a
                           v-if="getClubDirectionsUrl(game.club)"
                           :href="getClubDirectionsUrl(game.club)!"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="ml-1 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                        >
                           <i class="pi pi-map-marker text-[10px]"></i>
                           Directions
                        </a>
                     </div>
                  </div>

                  <!-- Ground -->
                  <div v-if="game.ground" class="flex items-center gap-2 text-sm">
                     <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                        <i class="pi pi-flag text-[10px] text-green-600"></i>
                     </span>
                     <div class="flex flex-wrap items-center gap-x-1">
                        <span class="text-gray-500">Field:</span>
                        <span class="text-gray-900">{{ getGroundName(game.ground) }}</span>
                        <a
                           v-if="getGroundDirectionsUrl(game.ground)"
                           :href="getGroundDirectionsUrl(game.ground)!"
                           target="_blank"
                           rel="noopener noreferrer"
                           class="ml-1 inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
                        >
                           <i class="pi pi-map-marker text-[10px]"></i>
                           Directions
                        </a>
                     </div>
                  </div>

                  <!-- Umpires -->
                  <div v-if="game.officials.umpire1 || game.officials.umpire2" class="flex items-center gap-2 text-sm">
                     <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-purple-100">
                        <i class="pi pi-user text-[10px] text-purple-600"></i>
                     </span>
                     <span class="text-gray-500">Umpires:</span>
                     <span class="text-gray-900">
                        {{ [game.officials.umpire1, game.officials.umpire2].filter(Boolean).join(', ') }}
                     </span>
                  </div>

                  <!-- Referee -->
                  <div v-if="game.officials.referee" class="flex items-center gap-2 text-sm">
                     <span class="flex size-6 shrink-0 items-center justify-center rounded-full bg-amber-100">
                        <i class="pi pi-id-card text-[10px] text-amber-600"></i>
                     </span>
                     <span class="text-gray-500">Referee:</span>
                     <span class="text-gray-900">{{ game.officials.referee }}</span>
                  </div>

                  <!-- Red Card -->
                  <div v-if="game.cards?.redCard" class="flex items-start gap-2 text-sm">
                     <span class="mt-0.5 inline-block h-4 w-2.5 -rotate-6 rounded-sm bg-red-600 shadow-sm"></span>
                     <div>
                        <span class="text-gray-500">Red Card:</span>
                        <span class="ml-1 text-gray-900">{{ game.cards.redCard }}</span>
                     </div>
                  </div>

                  <!-- Yellow Card -->
                  <div v-if="game.cards?.yellowCard" class="flex items-start gap-2 text-sm">
                     <span class="mt-0.5 inline-block h-4 w-2.5 -rotate-6 rounded-sm bg-yellow-400 shadow-sm"></span>
                     <div>
                        <span class="text-gray-500">Yellow Card:</span>
                        <span class="ml-1 text-gray-900">{{ game.cards.yellowCard }}</span>
                     </div>
                  </div>

                  <!-- Links Section -->
                  <div v-if="game.links.livestreamLink || game.links.multiMarkerMap || game.links.googleMapsLink" class="flex flex-wrap gap-3 pt-2">
                     <!-- Live Stream -->
                     <a
                        v-if="game.links.livestreamLink"
                        :href="game.links.livestreamLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs text-red-700 hover:bg-red-100"
                     >
                        <svg class="size-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                           <circle cx="12" cy="12" r="2" />
                           <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49" />
                        </svg>
                        Live Stream
                     </a>

                     <!-- Highlights -->
                     <button
                        v-if="game.links.multiMarkerMap"
                        class="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs text-blue-700 hover:bg-blue-100"
                     >
                        <i class="pi pi-play-circle text-xs"></i>
                        Highlights
                     </button>

                     <!-- Location -->
                     <a
                        v-if="game.links.googleMapsLink"
                        :href="game.links.googleMapsLink"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex items-center gap-1 rounded-full bg-green-50 px-3 py-1 text-xs text-green-700 hover:bg-green-100"
                     >
                        <i class="pi pi-map-marker text-xs"></i>
                        Location
                     </a>
                  </div>
               </div>
            </div>
         </template>
      </Panel>
   </div>
</template>

<style scoped>
.game-panel :deep(.p-panel-header) {
   padding: 0.5rem;
   background-color: white;
   border-radius: 0.5rem;
}

.game-panel :deep(.p-panel-content) {
   padding: 0.5rem;
   border-top: 1px solid #e5e7eb;
}

.game-panel :deep(.p-panel-toggler) {
   width: 1.5rem;
   height: 1.5rem;
}
</style>
